import React, { ReactElement, useState } from 'react';
import { useTranslations } from 'next-intl';
import { reportUserClickedOnEvent } from 'utils/analytics/segment';
import {
  requestAuthenticateWithApple,
  requestAuthenticateWithFacebook,
  requestAuthenticateWithGoogle,
  requestToCancelAuthentication,
  requestToLogout,
} from 'xstate/actors/AuthActor';
import { useAuthentication } from 'xstate/hooks/useAuthentication';
import SignInBtn from './SignInBtn';
import { AuthMethodType } from 'models/Auth';
import EmailSignIn, { EmailSignInStep } from './EmailSignIn';
import AuthStatus from './AuthStatus';
import Divider from 'elements/Divider';
import Link from 'next/link';
import classNames from 'classnames';

export enum SocialLoginLayout {
  // One full-width labelled button per provider, stacked.
  Expanded = 'expanded',
  // Icon-only provider buttons side by side on a single row.
  Compact = 'compact',
}

type Props = {
  header?: React.ReactNode;
  socialLayout?: SocialLoginLayout;
};

const DefaultHeader = (): ReactElement => {
  const t = useTranslations();
  return (
    <h2 className={styles.header.container}>
      <span className={styles.header.title}>{t('auth.title.default')}</span>
      <span className={styles.header.subtitle}>
        {t.rich('auth.subtitle.default', { br: () => <br /> })}
      </span>
    </h2>
  );
};

const SignIn = ({
  header,
  socialLayout = SocialLoginLayout.Compact,
}: Props): ReactElement => {
  const t = useTranslations();
  const [emailStep, setEmailStep] = useState<EmailSignInStep>('email');
  // Past the inline email field (name / code entry), the email flow takes over
  // the whole panel.
  const isEmailFlowActive = emailStep !== 'email';
  // The provider whose pop-up is open, so the waiting screen can name it.
  const [pendingMethod, setPendingMethod] = useState<AuthMethodType>();

  const { isAuthenticating, isAuthenticated, isUnauthenticated } =
    useAuthentication();

  const handleLogoutBtnClick = () => requestToLogout();

  const handleCancelBtnClick = () => requestToCancelAuthentication();

  const handleGoogleBtnClick = () => {
    reportUserClickedOnEvent('Sign in with Google', 'Login Page');
    setPendingMethod(AuthMethodType.Google);
    requestAuthenticateWithGoogle();
  };

  const handleFacebookBtnClick = () => {
    reportUserClickedOnEvent('Sign in with Facebook', 'Login Page');
    setPendingMethod(AuthMethodType.Facebook);
    requestAuthenticateWithFacebook();
  };

  const handleAppleBtnClick = () => {
    reportUserClickedOnEvent('Sign in with Apple', 'Login Page');
    setPendingMethod(AuthMethodType.Apple);
    requestAuthenticateWithApple();
  };

  const Header = () => header ?? <DefaultHeader />;

  return (
    <div className={styles.container}>
      {/* Header */}
      {!isEmailFlowActive && <Header />}

      {isAuthenticating && (
        <AuthStatus
          state="authenticating"
          authMethod={pendingMethod}
          onAction={handleCancelBtnClick}
        />
      )}
      {isAuthenticated && (
        <AuthStatus state="authenticated" onAction={handleLogoutBtnClick} />
      )}

      {/* Email sign-in */}
      {isUnauthenticated && (
        <EmailSignIn step={emailStep} onStepChange={setEmailStep} />
      )}

      {/* Social sign-in */}
      {isUnauthenticated && !isEmailFlowActive && (
        <>
          <div className="w-72">
            <Divider
              type="text"
              text={t('common.or').toUpperCase()}
              textPlacement="center"
            />
          </div>
          {socialLayout === SocialLoginLayout.Compact ? (
            <div className={styles.compactRow}>
              <SignInBtn
                compact
                authMethod={AuthMethodType.Apple}
                onClick={handleAppleBtnClick}
              />
              <SignInBtn
                compact
                authMethod={AuthMethodType.Google}
                onClick={handleGoogleBtnClick}
              />
              <SignInBtn
                compact
                authMethod={AuthMethodType.Facebook}
                onClick={handleFacebookBtnClick}
              />
            </div>
          ) : (
            <>
              <SignInBtn
                authMethod={AuthMethodType.Google}
                onClick={handleGoogleBtnClick}
              />
              <SignInBtn
                authMethod={AuthMethodType.Apple}
                onClick={handleAppleBtnClick}
              />
              <SignInBtn
                authMethod={AuthMethodType.Facebook}
                onClick={handleFacebookBtnClick}
              />
            </>
          )}
        </>
      )}

      <p className={styles.footer}>
        {t.rich('auth.terms', {
          termsLink: (chunks) => (
            <Link className={styles.link} href="/legal/terms-of-use">
              {chunks}
            </Link>
          ),
          privacyLink: (chunks) => (
            <Link className={styles.link} href="/legal/privacy-policy">
              {chunks}
            </Link>
          ),
        })}
      </p>
    </div>
  );
};

export default SignIn;

const styles = {
  container: 'flex flex-col items-center mx-auto space-y-5 pt-8',
  header: {
    container: 'mb-10 max-w-[550px] mx-auto',
    title: classNames(
      'text-4xl block mb-6 leading-[45px] font-semibold text-mf-off-black-950',
      'xl:mt-0 2xl:leading-[58px] xl:text-5xl',
    ),
    subtitle:
      'text-md sm:text-lg text-mf-gray-600 2xl:text-lg block text-balance',
  },
  compactRow: 'flex w-72 gap-3',
  link: 'underline',
  footer: 'text-xs text-mf-gray-400 text-center mt-4',
};
